import { createContext, useContext, useState } from 'react';
import environment from '../env';
import useSWR, { SWRConfig, mutate } from 'swr';
import useSWRImmutable from 'swr/immutable';
import { v4 as uuidv4 } from 'uuid';

import { useClient } from './ClientProvider';


const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext)

/**
 * Auth Provider
 * @param {*} param0 
 * @returns 
 */
const AuthProvider = ({ children }) => {

  /**
   * Authentication states
   */
  const authStates = {
    signedOut: "signedOut",
    signedIn: "signedIn",
    signUp: "signUp",
    confirmSignUp: "confirmSignUp",
    forgotPassword: "forgotPassword",
    confirmPassword: "confirmPassword",
    confirmOtp: "confirmOtp"
  }

  const { useGetConfig } = useClient();
  const { mutateClientConfig } = useGetConfig();

  const [accessToken, setAccessToken] = useState(localStorage.getItem("accessToken") ?? '');
  const [sessionId, setSessionId] = useState(localStorage.getItem("sessionId") ?? '')
  const [authState, setAuthState] = useState(authStates.signedOut);

  const [ otpUserId, setOtpUserId ] = useState(null);
  const [ otpSession, setOtpSession ] = useState(null);
  const [ otpDestination, setOtpDestination ] = useState(null);

  const apiKey = environment.CUSTOMER_PORTAL_API_KEY;
  const apiUrl = environment.CUSTOMER_PORTAL_API;

  const registrationReasons = [
    "24/7 access to account information",
    "24/7 help & assistance",
    "Update your account details",
  ];

  const passwordCriteria = [
    { id: 1, rule: "At least 10 characters" },
    { id: 2, rule: "At least 1 special character" },
    { id: 3, rule: "At least 1 lower case character" },
    { id: 4, rule: "At least 1 upper case character" },
    { id: 5, rule: "At least 1 number" }
  ];

  const defaultOptions = {
    method: "GET",
    headers: {
      "x-api-key": apiKey,
      "Authorization": `Bearer ${accessToken}`,
    },
  };

  /**
   * Get authenticated user SWR hook
   * @returns 
   */
  function useGetAuthUser() {
    const url = `${apiUrl}/auth/user`;
    console.info("Get authenticated user request: ", {defaultOptions, url})
    const { data, error, isLoading } = useSWRImmutable(accessToken ? [url, defaultOptions] : null, {
      refreshInterval: 60000,
    })
    console.info("Get authenticated user response: ", { data, error, isLoading })
    return {
      user: data,
      error,
      isLoading
    }
  }

  /**
   * Post IDV authentication callback
   * @param {*} url 
   * @param {*} arg 
   * @returns 
   */
  async function postIdvAuth(url, { arg }) {
    const { organisation, ...payload } = arg;

    const requestOptions = {
      ...defaultOptions,
      method: "POST",
      headers: {
        ...defaultOptions.headers,
        organisation, // Inject the required header for idv no postcode
      },
      body: JSON.stringify(payload),
    };

    console.info("post idv auth request: ", { url, requestOptions });

    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json());
  }


  /**
   * Post IDV with OTP authentication callback
   * @param {*} url 
   * @param {*} arg 
   * @returns 
   */
  async function postIdvOtpAuth(url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post idv with otp auth request: ", { url, requestOptions});
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  }

  /**
   * Post OTP confirmation callback callback
   * @param {*} url 
   * @param {*} arg 
   * @returns 
   */
  async function postOtpConfirmation(url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post otp confirmation request: ", { url, requestOptions});
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  }

  /**
   * Post sign out callback
   * @param {*} url 
   * @param {*} arg 
   * @returns 
   */
  async function postSignOut(url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post sign out request: ", { url, requestOptions});
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  }

  /**
   * Post user sign in callback
   * */
  async function postSignIn (url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post sign in request: ", { url, requestOptions });
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  };

  /**
   * Post confirm user sign in callback
   * @param {*} url 
   * @param {*} arg 
   * @returns 
   */
  async function postConfirmSignIn(url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post confirm sign in request: ", { url, requestOptions});
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  }

  /**
   * Post user sign up callback
   * */
  async function postSignUp (url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post sign up request: ", { url, requestOptions });
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  };

  /**
   * Post forgot password callback
   * */
  async function postForgotPassword (url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post forgot password request: ", { url, requestOptions });
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  };

  /**
   * Post confirm password callback
   * */
  async function postConfirmPassword (url, {arg} ) {
    let requestOptions = {
      ...defaultOptions,
      method: 'POST',
      body: JSON.stringify(arg)
    }
    console.info("post confirm password request: ", { url, requestOptions });
    return fetch(`${apiUrl}${url}`, requestOptions).then(res => res.json())
  };

  /**
   * Post confirm user sign up callback
  **/
  async function postConfirmSignUp(confirmationCode) {
    throw new Error('Confirm user sign up not yet implemented');
  };

  /**
   * Post resend confirmation code callback
   * */
  async function postResendConfirmationCode(user) {
    throw new Error('Resend confirmation code not yet implemented');
  };

  /**
   * Start session
   * */
  function startSession(authResult) {
    setAccessToken(authResult.accessToken);
    const newSessionId = uuidv4();
    setSessionId(newSessionId);
    localStorage.setItem('accessToken', authResult.accessToken);
    localStorage.setItem('authResult', JSON.stringify(authResult));
    localStorage.setItem('sessionId', newSessionId);
  };

  /**
   * End session
   * */
  function endSession() {
    setAccessToken(null);
    setSessionId(null);
    setAuthState(authStates.signedOut);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('authResult');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('remindMeLaterClicked');
    localStorage.removeItem('viewMyAccountClicked');
    localStorage.removeItem('startAccessibilityClicked');
    localStorage.removeItem('accessibilitySubmitted')
    // Clear all cache, then revalidate client config
    mutate(() => true, undefined, { revalidate: false });
    mutateClientConfig();
  };

  const contextValue = {
    accessToken,
    sessionId,
    authState,
    setAuthState,
    authStates,
    endSession,
    otpUserId,
    setOtpUserId,
    otpSession,
    setOtpSession,
    otpDestination,
    setOtpDestination,
    postConfirmSignUp,
    postResendConfirmationCode,
    postSignIn,
    postConfirmSignIn,
    postSignUp,
    postForgotPassword,
    postConfirmPassword,
    postIdvAuth,
    postIdvOtpAuth,
    postOtpConfirmation,
    postSignOut,
    startSession,
    useGetAuthUser,
    registrationReasons,
    passwordCriteria,
  }

  return (
    <AuthContext.Provider value={ contextValue }>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;